import { Router, watchBusy } from "@frui.ts/screens/dist";
import { attachAutomaticValidator } from "@frui.ts/validation";
import type { interfaces } from "inversify";
import { computed } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import { Modules } from "config";
import CreditExposureRepository from "repositories/creditExposureRepository";
import Distributor from "../../../../entities/distributor";
import OptionsService from "services/optionsService";
import SecurityService from "services/securityService";
import CreditExposureType from "entities/creditExposureType";
import EditableDetailViewModel from "../../../editableDetailViewModel";
import type CreditExposureDetailResponse from "entities/creditExposureDetailResponse";
import { CreditExposureDetailValidationRules } from "entities/creditExposureDetailResponse";
import CreditExposureState from "entities/creditExposureState";

@Router.registerRoute({ route: ":creditExposureId", name: Router.Self })
export default class CreditExposureDetailViewModel extends EditableDetailViewModel<CreditExposureDetailResponse> {
  readonly modelName = "creditExposure";

  constructor(
    protected itemId: string,
    protected distributorId: string,
    private repository: CreditExposureRepository,
    protected localizationService: LocalizationService,
    protected notificationService: NotificationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    private optionsService: OptionsService
  ) {
    super(itemId);
  }

  @watchBusy
  protected async loadDetail() {
    const item: CreditExposureDetailResponse = await this.repository.get(this.distributorId, this.itemId);
    attachAutomaticValidator(item, CreditExposureDetailValidationRules);
    return item;
  }

  protected async updateData() {
    await this.repository.update(this.item, this.distributorId, this.item.id);

    return Promise.resolve(this.item);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `creditExposures.${code}`, size);
  }

  @computed
  get creditExposureTypeOptions() {
    return this.optionsService.internalEnumOptions(CreditExposureType);
  }

  @computed
  get isEditable() {
    return (
      this.item?.state !== CreditExposureState.Canceled &&
      ((this.securityService.allowTo("distributors") && this.securityService.allowTo("enums")) ||
        this.securityService.allowToEntity(this.modelName, "write"))
    );
  }

  @computed
  get distributorOptions() {
    return this.optionsService.entityOptions(Distributor);
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string, distributorId: string) => {
      return new CreditExposureDetailViewModel(
        itemId,
        distributorId,
        container.get(CreditExposureRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(OptionsService)
      );
    };
  }
}
